import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  CreateKnowledgeTopicRequest,
  KnowledgeGenerateResponse,
  KnowledgeHubResponse,
  KnowledgePageDetailResponse,
  KnowledgePageDto,
  KnowledgePagesListResponse,
  KnowledgeTopicDto,
  KnowledgeTopicsListResponse,
  UpdateKnowledgeTopicRequest,
} from "@oracus/shared/api/knowledge";

import { apiClient } from "./client.ts";

const TOPICS_KEY = ["knowledge", "topics"] as const;
const HUB_KEY = ["knowledge", "hub"] as const;

export function useKnowledgeTopics() {
  return useQuery({
    queryKey: TOPICS_KEY,
    queryFn: () =>
      apiClient.get("/api/knowledge/topics", KnowledgeTopicsListResponse),
  });
}

export function useKnowledgeHub() {
  return useQuery({
    queryKey: HUB_KEY,
    queryFn: () => apiClient.get("/api/knowledge/hub", KnowledgeHubResponse),
  });
}

export function useCreateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateKnowledgeTopicRequest) =>
      apiClient.post("/api/knowledge/topics", body, KnowledgeTopicDto),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOPICS_KEY }),
  });
}

export function useUpdateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateKnowledgeTopicRequest;
    }) =>
      apiClient.patch(
        `/api/knowledge/topics/${id}`,
        body,
        KnowledgeTopicDto,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOPICS_KEY }),
  });
}

export function useDeleteTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/api/knowledge/topics/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOPICS_KEY }),
  });
}

export function useGenerateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(
        `/api/knowledge/topics/${id}/generate`,
        undefined,
        KnowledgeGenerateResponse,
      ),
    onSuccess: (_data, id) =>
      qc.invalidateQueries({ queryKey: ["knowledge", "pages", id] }),
  });
}

export function useTopicPages(topicId: string | null) {
  return useQuery({
    queryKey: ["knowledge", "pages", topicId],
    queryFn: () =>
      apiClient.get(
        `/api/knowledge/topics/${topicId}/pages`,
        KnowledgePagesListResponse,
      ),
    enabled: topicId !== null,
    // Generation runs async on the worker (no row until it finishes), so we
    // poll steadily while the panel is open — that's how a freshly-generated
    // draft appears without a manual refresh.
    refetchInterval: 5000,
  });
}

export function usePageDetail(pageId: string | null) {
  return useQuery({
    queryKey: ["knowledge", "page", pageId],
    queryFn: () =>
      apiClient.get(
        `/api/knowledge/pages/${pageId}`,
        KnowledgePageDetailResponse,
      ),
    enabled: pageId !== null,
  });
}

export function usePublishPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) =>
      apiClient.post(
        `/api/knowledge/pages/${pageId}/publish`,
        undefined,
        KnowledgePageDto,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TOPICS_KEY });
      qc.invalidateQueries({ queryKey: HUB_KEY });
      qc.invalidateQueries({ queryKey: ["knowledge", "pages"] });
      qc.invalidateQueries({ queryKey: ["knowledge", "page"] });
    },
  });
}

export function useRejectPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) =>
      apiClient.post(
        `/api/knowledge/pages/${pageId}/reject`,
        undefined,
        KnowledgePageDto,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["knowledge", "pages"] });
      qc.invalidateQueries({ queryKey: ["knowledge", "page"] });
    },
  });
}
